<template>
  <ObjectiveMoveModal
    :disable="isDisable"
    :loading="loading"
    :show="show"
    :title="t('objective.select_level')"
    @close="hideModal"
    @proceed="proceed"
    @prev-step="prevStep"
  >
    <AppInfoMessage :type="INFO_MESSAGE_TYPES.WARNING" class="slm-Subtitle">
      {{ t('move.select_levels_subtitle') }}
    </AppInfoMessage>
    <ObjectiveMoveConflictItems
      :conflict-items="conflicts"
      :get-hidden-items="getHiddenItems"
      :select-items="levelOptions"
      :select-placeholder="$t('objective.select_level')"
      :selected-items="selectedLevels"
      @update:model-value="updateConflict"
    >
      <template #prepend-input="{ conflict }">
        <OkrIcon
          :objective="getIconSelectedLevel(conflict[OKR_LEVEL_ENTITY_KEYS.ID], currentLevelOptions)"
        />
      </template>
      <template #prepend-select-icon="{ conflictIndex }">
        <OkrIcon
          :objective="getIconSelectedLevel(selectedLevels[conflictIndex], levelOptions)"
          class="sml-SelectIcon"
        />
      </template>
      <template #select-option-label="{ option }">
        <OkrTypeFieldOption v-if="option" :option="option" />
      </template>
    </ObjectiveMoveConflictItems>
  </ObjectiveMoveModal>
</template>

<script setup>
import { nextTick, ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import LevelApiHandler from '@/api/level'
import { INFO_MESSAGE_TYPES } from '@/utils/components-configurations/app-info-message'
import { OKR_LEVEL_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { levelToOkrIconElement } from '@/utils/objectives'

import OkrTypeFieldOption from '@/components/form/OkrTypeFieldOption'
import OkrIcon from '@/components/objectives/items/OkrIcon'
import ObjectiveMoveConflictItems from '@/components/objectives/objective-move/ObjectiveMoveConflictItems'
import ObjectiveMoveModal from '@/components/objectives/objective-move/ObjectiveMoveModal'
import AppInfoMessage from '@/components/ui/AppInfoMessage'

defineOptions({
  name: 'SelectMoveLevel'
})

const props = defineProps({
  show: {
    type: Boolean
  },

  formModel: {
    type: Object,
    required: true
  },

  conflicts: {
    type: Array,
    default: () => []
  },

  loading: {
    type: Boolean
  }
})
const { t } = useI18n()
const emit = defineEmits({
  'update:show': null,
  proceed: null,
  'prev-step': null
})

const hideModal = async () => {
  emit('update:show', false)
  await nextTick()
}

const proceed = () => {
  const conflictLevels = props.conflicts.map((levelItem, levelItemIndex) => {
    return {
      ...levelItem,
      idToChange: selectedLevels.value[levelItemIndex]
    }
  })
  emit('proceed', conflictLevels)
}
const prevStep = () => {
  emit('prev-step')
}

const selectedLevels = ref(props.conflicts.map(conflict => conflict.idToChange))
const levelOptions = ref([])

const updateConflict = ({ index, value }) => {
  selectedLevels.value[index] = value
}

const getIconSelectedLevel = (levelId, options) => {
  const level = options.find(level => level[OKR_LEVEL_ENTITY_KEYS.ID] === levelId)
  if (!level) return {}
  return levelToOkrIconElement(level)
}

const getLevels = async () => {
  try {
    const levelApi = new LevelApiHandler()
    levelOptions.value = await levelApi.getLevels({
      workspaceIds: [props.formModel.workspaceId],
      levelIds: null
    })
  } catch (error) {
    handleError({ error })
  }
}
onMounted(() => {
  getLevels()
})

const currentLevelOptions = ref([])
const getCurrentLevels = async () => {
  try {
    const levelApi = new LevelApiHandler()
    currentLevelOptions.value = await levelApi.getLevels({
      workspaceIds: null,
      levelIds: null
    })
  } catch (error) {
    handleError({ error })
  }
}

const isDisable = computed(() => {
  return selectedLevels.value.some(level => !level)
})

const getHiddenItems = conflict => {
  const conflictedLevel = currentLevelOptions.value.find(
    level => level[OKR_LEVEL_ENTITY_KEYS.ID] === conflict[OKR_LEVEL_ENTITY_KEYS.ID]
  )

  if (!conflictedLevel) return []

  return levelOptions.value.reduce((acc = [], level) => {
    if (level[OKR_LEVEL_ENTITY_KEYS.TYPE_ID] !== conflictedLevel[OKR_LEVEL_ENTITY_KEYS.TYPE_ID]) {
      acc.push(level[OKR_LEVEL_ENTITY_KEYS.ID])
    }
    return acc
  }, [])
}

onMounted(() => {
  getCurrentLevels()
})
</script>

<style lang="scss" scoped>
.slm-Subtitle {
  margin-bottom: 35px;
}
</style>
